import type { I18nTranslator } from '@affine/i18n';
import type {
  AppAccessAudience,
  AppAudienceAccess,
  AppRowScope,
} from '@streamlain/view-config';

/** The order in which a page sentence names its audiences. */
export const ACCESS_SENTENCE_AUDIENCES = [
  'internal',
  'portal',
  'public',
] as const satisfies readonly AppAccessAudience[];

/** One right beyond reading, in the order the sentence names them. */
export type AccessSentenceExtra =
  | 'create'
  | 'updateOwn'
  | 'updateRead'
  | 'deleteOwn'
  | 'deleteRead';

export interface AccessSentenceClause {
  readonly audience: AppAccessAudience;
  readonly read: AppRowScope;
  readonly extras: readonly AccessSentenceExtra[];
}

/** No audience reaches the page, or the active audiences with their rights. */
export type AccessSentence =
  | { readonly kind: 'nobody' }
  | {
      readonly kind: 'audiences';
      readonly clauses: readonly AccessSentenceClause[];
    };

/**
 * What one page grants, as the parts of one everyday sentence: per active
 * audience the rows it reads and what it may do beyond reading.
 */
export function accessSentence(
  audiences: Readonly<
    Partial<Record<AppAccessAudience, AppAudienceAccess | undefined>>
  >
): AccessSentence {
  const clauses = ACCESS_SENTENCE_AUDIENCES.flatMap(audience => {
    const access = audiences[audience];
    if (!access?.active) return [];
    const extras: AccessSentenceExtra[] = [];
    if (access.create) extras.push('create');
    if (access.update !== 'off')
      extras.push(access.update === 'own' ? 'updateOwn' : 'updateRead');
    if (access.delete !== 'off')
      extras.push(access.delete === 'own' ? 'deleteOwn' : 'deleteRead');
    return [{ audience, read: access.read, extras }];
  });
  return clauses.length === 0
    ? { kind: 'nobody' }
    : { kind: 'audiences', clauses };
}

const WHO_KEYS = {
  internal: 'com.streamlain.appBases.access.advisor.who.internal',
  portal: 'com.streamlain.appBases.access.advisor.who.portal',
  public: 'com.streamlain.appBases.access.advisor.who.public',
} as const;

const READ_KEYS = {
  own: 'com.streamlain.appBases.access.advisor.read.own',
  group: 'com.streamlain.appBases.access.advisor.read.group',
  all: 'com.streamlain.appBases.access.advisor.read.all',
} as const;

const EXTRA_KEYS = {
  create: 'com.streamlain.appBases.access.advisor.extra.create',
  updateOwn: 'com.streamlain.appBases.access.advisor.extra.updateOwn',
  updateRead: 'com.streamlain.appBases.access.advisor.extra.updateRead',
  deleteOwn: 'com.streamlain.appBases.access.advisor.extra.deleteOwn',
  deleteRead: 'com.streamlain.appBases.access.advisor.extra.deleteRead',
} as const;

/**
 * Says a sentence in the translator's language. The Studio memoises it per
 * language; the goal panel uses the app-wide translator.
 */
export function accessSentenceFormatter(
  t: I18nTranslator
): (sentence: AccessSentence) => string {
  const list = new Intl.ListFormat(t.language, { type: 'conjunction' });
  const clause = (part: AccessSentenceClause) =>
    t['com.streamlain.appBases.access.advisor.clause']({
      who: t[WHO_KEYS[part.audience]](),
      rights: list.format([
        t[READ_KEYS[part.read]](),
        ...part.extras.map(extra => t[EXTRA_KEYS[extra]]()),
      ]),
    });
  return sentence =>
    sentence.kind === 'nobody'
      ? t['com.streamlain.appBases.access.advisor.nobody']()
      : t['com.streamlain.appBases.access.advisor.sentence']({
          clauses: sentence.clauses.map(clause).join('; '),
        });
}
